import React, { useState, useEffect, useRef } from 'react';

interface Voice {
  name: string;
  lang: string;
  voiceURI: string;
  localService: boolean;
}

/**
 * Web Speech API Prototype
 * Tests browser-native TTS capabilities
 *
 * Features to test:
 * - Voice quality and naturalness
 * - Available voices (especially iOS Siri voices)
 * - Speed control (0.5x - 2.0x)
 * - Offline support
 * - Latency (time to first audio)
 * - Background playback
 */
export const WebSpeechPrototype: React.FC = () => {
  const [text, setText] = useState(
    "The Web Speech API provides two distinct areas of functionality — speech recognition, and speech synthesis (also known as text to speech, or tts) — which open up interesting new possibilities for accessibility, and control mechanisms. This article provides a simple introduction to both areas, along with demos."
  );
  const [voices, setVoices] = useState<Voice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [speed, setSpeed] = useState<number>(1.0);
  const [pitch, setPitch] = useState<number>(1.0);
  const [volume, setVolume] = useState<number>(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [totalCharacters, setTotalCharacters] = useState(0);
  const [latency, setLatency] = useState<number | null>(null);

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const startTimeRef = useRef<number>(0);

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      const voiceList: Voice[] = availableVoices.map(voice => ({
        name: voice.name,
        lang: voice.lang,
        voiceURI: voice.voiceURI,
        localService: voice.localService
      }));
      setVoices(voiceList);

      // Pre-select enhanced iOS voice if available, otherwise first voice
      const enhancedVoice = voiceList.find(v =>
        v.name.includes('Enhanced') ||
        v.name.includes('Premium') ||
        v.name.includes('Samantha')
      );
      if (enhancedVoice) {
        setSelectedVoice(enhancedVoice.voiceURI);
      } else if (voiceList.length > 0) {
        setSelectedVoice(voiceList[0].voiceURI);
      }
    };

    // Voices may not be immediately available on some browsers
    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const handleSpeak = () => {
    if (!text.trim()) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // Measure latency
    startTimeRef.current = performance.now();

    // Create utterance
    const utterance = new SpeechSynthesisUtterance(text);

    // Set voice
    const voice = voices.find(v => v.voiceURI === selectedVoice);
    if (voice) {
      const synthVoice = window.speechSynthesis.getVoices().find(
        v => v.voiceURI === voice.voiceURI
      );
      if (synthVoice) {
        utterance.voice = synthVoice;
      }
    }

    // Set properties
    utterance.rate = speed;
    utterance.pitch = pitch;
    utterance.volume = volume;

    // Event handlers
    utterance.onstart = () => {
      const endTime = performance.now();
      const latencyMs = endTime - startTimeRef.current;
      setLatency(latencyMs);
      setIsPlaying(true);
      setIsPaused(false);
      setTotalCharacters(text.length);
      console.log(`🎤 TTS started. Latency: ${latencyMs.toFixed(0)}ms`);
    };

    utterance.onboundary = (event) => {
      // Track progress (word boundaries)
      setCurrentPosition(event.charIndex);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentPosition(0);
      console.log('🎤 TTS ended');
    };

    utterance.onerror = (event) => {
      console.error('🎤 TTS error:', event);
      setIsPlaying(false);
      setIsPaused(false);
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const handlePause = () => {
    if (isPlaying && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const handleResume = () => {
    if (isPlaying && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentPosition(0);
  };

  const progress = totalCharacters > 0 ? (currentPosition / totalCharacters) * 100 : 0;

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white">
          Web Speech API Prototype
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Test browser-native TTS (free, offline-capable on iOS/Android)
        </p>
      </div>

      {/* Voice Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Voice ({voices.length} available)
        </label>
        <select
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={isPlaying}
        >
          {voices.map((voice) => (
            <option key={voice.voiceURI} value={voice.voiceURI}>
              {voice.name} ({voice.lang}) {voice.localService ? '📱' : '☁️'}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          📱 = Local (offline), ☁️ = Cloud (requires internet)
        </p>
      </div>

      {/* Text Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Text to speak ({text.length} characters)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full h-32 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
          placeholder="Enter text to convert to speech..."
          disabled={isPlaying}
        />
      </div>

      {/* Speed Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Speed: {speed.toFixed(1)}x
        </label>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={speed}
          onChange={(e) => setSpeed(parseFloat(e.target.value))}
          className="w-full"
          disabled={isPlaying}
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>0.5x (Slow)</span>
          <span>1.0x (Normal)</span>
          <span>2.0x (Fast)</span>
        </div>
      </div>

      {/* Pitch Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Pitch: {pitch.toFixed(1)}
        </label>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={pitch}
          onChange={(e) => setPitch(parseFloat(e.target.value))}
          className="w-full"
          disabled={isPlaying}
        />
      </div>

      {/* Volume Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Volume: {(volume * 100).toFixed(0)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={volume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          className="w-full"
          disabled={isPlaying}
        />
      </div>

      {/* Progress Bar */}
      {isPlaying && (
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span>Progress</span>
            <span>{progress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Latency Display */}
      {latency !== null && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
            ⚡ Time to first audio: <span className="font-bold">{latency.toFixed(0)}ms</span>
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
            {latency < 100 ? '✅ Excellent (instant)' :
             latency < 300 ? '✅ Good (fast)' :
             latency < 500 ? '⚠️ Acceptable' :
             '❌ Slow (optimize needed)'}
          </p>
        </div>
      )}

      {/* Playback Controls */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={handleSpeak}
          disabled={isPlaying || !text.trim()}
          className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {isPlaying ? '🎤 Speaking...' : '▶️ Speak'}
        </button>

        {isPlaying && !isPaused && (
          <button
            onClick={handlePause}
            className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-medium rounded-lg transition-colors"
          >
            ⏸️ Pause
          </button>
        )}

        {isPlaying && isPaused && (
          <button
            onClick={handleResume}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            ▶️ Resume
          </button>
        )}

        {isPlaying && (
          <button
            onClick={handleStop}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
          >
            ⏹️ Stop
          </button>
        )}
      </div>

      {/* Testing Checklist */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900">
        <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
          📋 Testing Checklist
        </h3>
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPhone 16 Pro (iOS Safari) - Check for "Samantha (Enhanced)" voice</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on Google Pixel 9 (Chrome) - Check for Google Neural voices</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test on iPad - Verify voice quality</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Enable airplane mode - Test offline playback</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Lock screen - Test background playback (pause/resume)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Measure latency (should be &lt;300ms for good UX)</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Test speed control (0.5x - 2.0x) - Verify quality at all speeds</label>
          </div>
          <div className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" />
            <label>Rate voice naturalness (1-10 scale)</label>
          </div>
        </div>
      </div>

      {/* Device Info */}
      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg">
        <h4 className="font-bold text-sm mb-2 text-gray-900 dark:text-white">Device Info:</h4>
        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
          <p><strong>User Agent:</strong> {navigator.userAgent}</p>
          <p><strong>Platform:</strong> {navigator.platform}</p>
          <p><strong>Speech Synthesis Supported:</strong> {window.speechSynthesis ? '✅ Yes' : '❌ No'}</p>
        </div>
      </div>
    </div>
  );
};

export default WebSpeechPrototype;
